/**
 * WarpFieldGenPass — runs the warp_field_gen shader to populate one
 * displacement texture per invocation. Cheap (256x256 fragment shader, 1 draw)
 * so we just re-encode it whenever the WarpFieldSystem decides to regenerate.
 */

import genWgsl from '../shaders/warp_field_gen.wgsl?raw';

/** Mirror of the integer ids in warp_field_gen.wgsl. */
export const enum FieldGeneratorId {
  Curl = 0,
  Spiral = 1,
  Swirl = 2,
  Julia = 3,
}

export type FieldGenInput = {
  kind: FieldGeneratorId;
  seed: number;
  centerX: number;
  centerY: number;
  scale: number;
  param0: number;
  param1: number;
  param2: number;
};

// 1 u32 + 7 f32 = 32 bytes (aligned to 16).
const PARAMS_BYTES = 32;

export class WarpFieldGenPass {
  private readonly device: GPUDevice;
  private readonly pipeline: GPURenderPipeline;
  private readonly layout: GPUBindGroupLayout;
  private readonly paramsBuffer: GPUBuffer;
  private readonly cpuParams = new ArrayBuffer(PARAMS_BYTES);
  private readonly cpuParamsF32 = new Float32Array(this.cpuParams);
  private readonly cpuParamsU32 = new Uint32Array(this.cpuParams);

  constructor(device: GPUDevice, format: GPUTextureFormat) {
    this.device = device;

    this.paramsBuffer = device.createBuffer({
      label: 'warp-field-gen-params',
      size: PARAMS_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const shader = device.createShaderModule({
      label: 'warp-field-gen',
      code: genWgsl,
    });

    this.layout = device.createBindGroupLayout({
      label: 'warp-field-gen-bgl',
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: 'uniform' },
        },
      ],
    });

    this.pipeline = device.createRenderPipeline({
      label: 'warp-field-gen-pipeline',
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.layout] }),
      vertex: { module: shader, entryPoint: 'vs_main' },
      fragment: {
        module: shader,
        entryPoint: 'fs_main',
        targets: [{ format }],
      },
      primitive: { topology: 'triangle-list' },
    });
  }

  /** Encodes a single full-target draw that fills `targetView` with the field. */
  encode(
    encoder: GPUCommandEncoder,
    targetView: GPUTextureView,
    input: FieldGenInput,
  ): void {
    this.cpuParamsU32[0] = input.kind >>> 0;
    this.cpuParamsF32[1] = input.seed;
    this.cpuParamsF32[2] = input.centerX;
    this.cpuParamsF32[3] = input.centerY;
    this.cpuParamsF32[4] = input.scale;
    this.cpuParamsF32[5] = input.param0;
    this.cpuParamsF32[6] = input.param1;
    this.cpuParamsF32[7] = input.param2;
    this.device.queue.writeBuffer(this.paramsBuffer, 0, this.cpuParams);

    const bindGroup = this.device.createBindGroup({
      label: 'warp-field-gen-bg',
      layout: this.layout,
      entries: [
        { binding: 0, resource: { buffer: this.paramsBuffer } },
      ],
    });

    const pass = encoder.beginRenderPass({
      label: 'warp-field-gen-pass',
      colorAttachments: [
        {
          view: targetView,
          loadOp: 'clear',
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          storeOp: 'store',
        },
      ],
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, bindGroup);
    pass.draw(6, 1, 0, 0);
    pass.end();
  }
}
